import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useHistorySocial } from '@hooks/register/history/use-history-social';
import { useLocalize } from '@hooks/use-localize';
import { useScrollToHash } from '@hooks/use-scroll-to-hash';
import { useStepForm } from '@hooks/use-step-form';
import { createHistorySocialSchema } from '@views/register/history/history-social.schema';
import { BoolRadioField } from '@views/register/history/history-fields';
import { asString, asStringBool } from '@/utils/parse';
import { DashRegistrationStep, historyLinkMap } from '@/types/dashboard';
import type { IHistorySocialFormValues, IHistorySocialModel, IHistorySocialSubmitResult } from '@/types/history/history-social';
import TobaccoSection from '@views/register/history/social-sections/tobacco-section';
import AlcoholSection from '@views/register/history/social-sections/alcohol-section';
import DrugsSection from '@views/register/history/social-sections/drugs-section';
import LegalTravelSection from '@views/register/history/social-sections/legal-travel-section';
import RiskQuestionsSection from '@views/register/history/social-sections/risk-questions-section';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';

const HistorySocial = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useHistorySocial(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <HistorySocialContent model={model} submit={submit} isSubmitting={isSubmitting} submitError={submitError} />
        </PageLoader>
    );
};

interface IHistorySocialContentProps {
    model: IHistorySocialModel;
    submit: (values: IHistorySocialFormValues) => Promise<IHistorySocialSubmitResult>;
    isSubmitting: boolean;
    submitError: string | null;
}

const HistorySocialContent = ({ model, submit, isSubmitting, submitError }: IHistorySocialContentProps) => {
    const t = useLocalize(model.languageData);

    useScrollToHash();

    const drugRows = model.drugUseHistory.map((x) => ({
        recreationalDrug: asString(x.recreationalDrug),
        yearsAgo: asString(x.yearsAgo),
        hasAbusedDrug: asStringBool(x.hasAbusedDrug),
        hasReceivedTreatment: asStringBool(x.hasReceivedTreatment),
    }));

    const form = useStepForm<IHistorySocialFormValues, IHistorySocialSubmitResult>({
        resolver: zodResolver(
            createHistorySocialSchema(model),
        ) as unknown as Resolver<IHistorySocialFormValues>,
        defaultValues: {
            tobaccoYears: asString(model.tobaccoYears),
            tobaccoPacks: asString(model.tobaccoPacks),
            tobaccoFrequency: asString(model.tobaccoFrequency),

            hasUsedAlcohol: asStringBool(model.hasUsedAlcohol),
            alcoholDrinks: asString(model.alcoholDrinks),
            alcoholFrequency: asString(model.alcoholFrequency),
            usesAlcohol: asStringBool(model.usesAlcohol),
            hasAbusedAlcohol: asStringBool(model.hasAbusedAlcohol),

            hasUsedDrugs: asStringBool(model.hasUsedDrugs),
            drugUseHistory:
                drugRows.length > 0
                    ? drugRows
                    : [
                        {
                            recreationalDrug: '',
                            yearsAgo: '',
                            hasAbusedDrug: '',
                            hasReceivedTreatment: '',
                        },
                    ],

            hasHadLegalIssues: asStringBool(model.hasHadLegalIssues),
            hasBeenSentencedToPrison: asStringBool(model.hasBeenSentencedToPrison),
            prisonWhenAndWhere: model.prisonWhenAndWhere,
            hasTraveledLastYear: asStringBool(model.hasTraveledLastYear),
            travelLocations: model.travelLocations,

            hasHadHighRiskSex: asStringBool(model.hasHadHighRiskSex),
            hasHadSexWithMmRelationsMale: asStringBool(model.hasHadSexWithMmRelationsMale),
            hasHadMmSex: asStringBool(model.hasHadMmSex),
            hasHadSexWithProstitute: asStringBool(model.hasHadSexWithProstitute),
            hasProstituted: asStringBool(model.hasProstituted),
            hasHadSexWithNeedleDrugUser: asStringBool(model.hasHadSexWithNeedleDrugUser),
            hasUsedNeedleDrugs: asStringBool(model.hasUsedNeedleDrugs),
            hasBeenInJailOver72: asStringBool(model.hasBeenInJailOver72),
            hasHadStd: asStringBool(model.hasHadStd),
            hasBeenOnHemodialysis: asStringBool(model.hasBeenOnHemodialysis),
            hasHadSuicidalThoughtsAttempts: asStringBool(model.hasHadSuicidalThoughtsAttempts),
        },
        submit,
        linkMap: historyLinkMap,
    });

    return (
        <StepFormLayout
            kind="history"
            model={model}
            step={DashRegistrationStep.HistorySocial}
            title={t('DashLabel_SocialHistory', 'Social History')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            {model.hasUsedTobacco === true && (
                <div id="tobacco">
                    <TobaccoSection model={model} t={t} />
                </div>
            )}

            <div id="alcohol">
                <AlcoholSection model={model} t={t} />
            </div>

            <div id="drugs">
                <DrugsSection model={model} t={t} />
            </div>

            <div id="legal-travel">
                <LegalTravelSection t={t} />
            </div>

            {model.askSexualSocialQuestions && (
                <div id="risk-questions">
                    <RiskQuestionsSection model={model} t={t} />
                </div>
            )}

            <BoolRadioField
                name="hasHadSuicidalThoughtsAttempts"
                label={t('DashLabel_HaveYouHadAnySuicidalThoughtsOrAttemptsInTheLastFi', 'Have you had any suicidal thoughts or attempts in the last five years')}
                yesLabel={t('Word_Yes', 'Yes')}
                noLabel={t('Word_No', 'No')}
            />
        </StepFormLayout>
    );
};

export default HistorySocial;
